import { useCallback } from 'react';
import type { CalloutId } from '@sorrem/shared';
import { createValueStore } from '@sorrem-client/ui/shared/value-store';
import { useAccountSetting } from './use-account-setting';
import { useIsScreenCovered } from './use-screen-cover';
import { useIsTourRunning } from './use-tour-activity';
import { useValueStoreSelector } from './value-store';

const NO_DISMISSED_CALLOUTS: CalloutId[] = [];

/** Callouts closed for now: hidden until the page next loads. */
const closedThisVisit = createValueStore<ReadonlySet<CalloutId>>(new Set());

const selectClosed = (closed: ReadonlySet<CalloutId>): ReadonlySet<CalloutId> => closed;

function closeForVisit(id: CalloutId): void {
    closedThisVisit.set((closed) => new Set([...closed, id]));
}

export interface CalloutState {
    /** Whether the callout shows: not dismissed on the account, not closed this visit, and nothing (a cutscene, a guided tour) covering the screen. False until settings load, so it never flashes. */
    isOpen: boolean;
    /** Hides it until the page next loads. */
    close: () => void;
    /** Opts the account out of it for good, everywhere. Also how a callout retires once its job is done. */
    dismiss: () => void;
}

/** One first-time callout's state (see callouts.ts in @sorrem/shared). */
export function useCallout(id: CalloutId): CalloutState {
    const { value: dismissed, isLoaded, set } = useAccountSetting('dismissedCallouts', NO_DISMISSED_CALLOUTS);
    const closed = useValueStoreSelector(closedThisVisit, selectClosed);
    const isScreenCovered = useIsScreenCovered();
    // A guided tour covers what a callout would point at (#234); the callout waits for it.
    const isTourRunning = useIsTourRunning();

    const close = useCallback(() => closeForVisit(id), [id]);
    const dismiss = useCallback((): void => {
        set((current) => (current.includes(id) ? current : [...current, id]));
    }, [id, set]);

    return { isOpen: isLoaded && !dismissed.includes(id) && !closed.has(id) && !isScreenCovered && !isTourRunning, close, dismiss };
}

interface ResetCalloutsState {
    /** Whether any callout is hidden: opted out of, retired, or closed this visit. */
    hasHidden: boolean;
    /** Shows every callout again: clears the account's opt-outs and what was closed this visit. */
    reset: () => void;
}

/** The settings action that brings back every first-time callout. */
export function useResetCallouts(): ResetCalloutsState {
    const { value: dismissed, set } = useAccountSetting('dismissedCallouts', NO_DISMISSED_CALLOUTS);
    const hasClosed = useValueStoreSelector(closedThisVisit, (closed) => closed.size > 0);
    const dismissedCount = dismissed.length;

    const reset = useCallback((): void => {
        closedThisVisit.set(new Set());
        set((current) => (current.length === 0 ? current : []));
    }, [set]);

    return { hasHidden: dismissedCount > 0 || hasClosed, reset };
}
